import React from 'react';
import { filterContacts, initialsOf } from '../../utils/phoneContacts';
import { maskNumber } from '../../utils/contactMatch';
import { CheckBox, InitialsDisc, pill } from './onboardingUi';

/**
 * The phone's contacts as pick rows (sign-up v3 CONTACTS card B, INVITE FRIENDS step 2): a 40px
 * initials disc, the name, `250 ··· 0143 · NOT ON PORTAL`, a 22px checkbox; rows divided by a
 * 1px #1F1F1F rule. A search pill above the list once there is more than a handful. `picked` is a
 * Set of contact ids; `cap` greys the rest once it is reached (further taps are ignored by the
 * owner's `onToggle`). `onPortal` marks a row as already here (`· ON PORTAL`, no checkbox cap).
 *
 * The list is `rows`: `[{ contactId, name, number, e164 }]` — one per contact, their first usable
 * number — which is what `matchContacts()` hands back as `others`.
 */
export default function ContactPickList({ rows, picked, onToggle, cap = Infinity, query = '', onQuery, maxHeight = 320, emptyLabel = 'NOBODY MATCHES', tagFor }) {
  const all = rows || [];
  const shown = filterContacts(all.map((r) => ({ ...r, phones: [{ number: r.e164 || r.number }] })), query);
  const full = picked.size >= cap;
  return (
    <div data-testid="contact-list" style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
      {onQuery && all.length > 5 && (
        <input
          value={query}
          onChange={(e) => onQuery(e.target.value)}
          placeholder="Search your contacts"
          aria-label="Search contacts"
          inputMode="search"
          autoCorrect="off"
          autoCapitalize="none"
          className="pe-in"
          data-testid="contact-search"
          style={{ ...pill(), height: 44, fontSize: 13 }}
        />
      )}
      <div style={{ display: 'flex', flexDirection: 'column', maxHeight, overflowY: 'auto', WebkitOverflowScrolling: 'touch', overscrollBehavior: 'contain' }}>
        {shown.map((r, i) => {
          const on = picked.has(r.contactId);
          const dim = !on && full;
          const tag = tagFor ? tagFor(r) : 'NOT ON PORTAL';
          return (
            <button
              key={r.contactId}
              type="button"
              onClick={() => onToggle(r)}
              aria-pressed={on}
              data-testid="contact-row"
              style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '12px 4px', border: 0, borderTop: i === 0 ? 0 : '1px solid #1F1F1F', background: 'transparent', color: '#fff', fontFamily: 'inherit', textAlign: 'left', cursor: dim ? 'default' : 'pointer', opacity: dim ? 0.45 : 1, transition: 'opacity .2s', width: '100%', boxSizing: 'border-box', WebkitTapHighlightColor: 'transparent' }}
            >
              <InitialsDisc initials={initialsOf(r.name)} on={on} />
              <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 3 }}>
                <span style={{ fontSize: 13, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.name}</span>
                <span style={{ fontSize: 10, letterSpacing: '.08em', color: '#6B6B6B', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{maskNumber(r.e164 || r.number)}{tag ? ` · ${tag}` : ''}</span>
              </span>
              <CheckBox on={on} />
            </button>
          );
        })}
        {!shown.length && <span style={{ padding: '14px 4px', fontSize: 10, fontWeight: 800, letterSpacing: '.14em', color: '#6B6B6B' }}>{emptyLabel}</span>}
      </div>
    </div>
  );
}
